import React, { useState } from 'react'
import Layout from '../components/Layout'
import { toolsAPI } from '../services/api'

const AppsPage = () => {
  const [selectedApp, setSelectedApp] = useState(null)

  const apps = [
    {
      id: 'background-remover',
      name: 'Background Remover',
      description: 'Automatically remove backgrounds from images using AI technology',
      icon: (
        <svg className="w-12 h-12 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
        </svg>
      ),
      category: 'Image Processing',
      features: [
        'AI-powered background removal',
        'Batch processing support',
        'High-quality edge detection',
        'Multiple output formats'
      ],
      status: 'Available',
      comingSoon: false
    }
  ]

  const handleAppClick = (app) => {
    setSelectedApp(app)
  }

  const [bgFile, setBgFile] = useState(null)
  const [bgLoading, setBgLoading] = useState(false)
  const [bgResultUrl, setBgResultUrl] = useState('')

  const handleLaunchApp = (app) => {
    if (app.id === 'background-remover') {
      setSelectedApp(app)
      return
    }
    alert(`Launching ${app.name}... (This is a demo app)`) // default for others
  }

  const submitBackgroundRemove = async (e) => {
    e.preventDefault()
    if (!bgFile) return
    try {
      setBgLoading(true)
      setBgResultUrl('')
      const res = await toolsAPI.backgroundRemove(bgFile)
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'image/png' })
      const url = URL.createObjectURL(blob)
      setBgResultUrl(url)
    } catch (err) {
      alert('Background removal failed')
    } finally {
      setBgLoading(false)
    }
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Apps</h1>
            <p className="text-gray-600">
              Powerful tools and utilities to enhance your workflow
            </p>
          </div>

          {/* Apps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {apps.map((app) => (
              <div
                key={app.id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 cursor-pointer"
                onClick={() => handleAppClick(app)}
              >
                <div className="p-6">
                  {/* App Icon and Header */}
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="flex-shrink-0">
                      {app.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {app.name}
                      </h3>
                      <p className="text-sm text-gray-500 mb-2">
                        {app.category}
                      </p>
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          app.status === 'Available' 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {app.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4">
                    {app.description}
                  </p>

                  {/* Features */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Key Features:</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {app.features.slice(0, 3).map((feature, index) => (
                        <li key={index} className="flex items-center">
                          <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Launch Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleLaunchApp(app)
                    }}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
                  >
                    Launch App
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Coming Soon Section */}
          <div className="mt-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Coming Soon</h2>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">More Apps Coming Soon</h3>
                <p className="text-gray-600">
                  We're working on additional tools and utilities to enhance your workflow. 
                  Stay tuned for updates!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* App Detail Modal */}
        {selectedApp && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                {/* Modal Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    {selectedApp.icon}
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{selectedApp.name}</h2>
                      <p className="text-gray-600">{selectedApp.category}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedApp(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>

                {/* App Body */}
                {selectedApp.id === 'background-remover' ? (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Remove Background</h3>
                    <form onSubmit={submitBackgroundRemove} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Select Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setBgFile(e.target.files?.[0] || null)}
                          className="w-full px-3 py-2 border rounded-lg"
                          required
                        />
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          type="submit"
                          disabled={bgLoading || !bgFile}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          {bgLoading ? 'Processing...' : 'Remove Background'}
                        </button>
                        {bgResultUrl && (
                          <a
                            href={bgResultUrl}
                            download="background-removed.png"
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            Download Result
                          </a>
                        )}
                      </div>
                    </form>
                    {bgResultUrl && (
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Preview</h4>
                        <img src={bgResultUrl} alt="Background removed" className="max-h-96 rounded-lg border" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">About</h3>
                    <p className="text-gray-600">{selectedApp.description}</p>
                  </div>
                )}

                {/* All Features */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Features</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedApp.features.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setSelectedApp(null)
                      setBgFile(null)
                      if (bgResultUrl) URL.revokeObjectURL(bgResultUrl)
                      setBgResultUrl('')
                      setBgLoading(false)
                    }}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}

export default AppsPage
